import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

/** Un chat de la columna izquierda: un cliente, su último mensaje. */
export type Conversacion = {
  numero: string
  titulo: string
  ultimoTexto: string | null
  ultimaDireccion: 'entrante' | 'saliente'
  ultimoEn: string
  total: number
  ordenTrabajoId: string | null
  /**
   * Si WhatsApp deja escribir sin plantilla: 24 horas desde el último mensaje
   * de la otra parte. Lo resuelve la vista y no el navegador, cuya hora puede
   * ir desfasada (0164).
   */
  sePuedeEscribir: boolean
  /** Quién la tiene tomada, si alguien la tiene. Mientras tanto el bot calla. */
  atendidaPor: string | null
  atendidaPorNombre: string | null
  /** Si el número es de la casa. A los clientes el asistente nunca responde. */
  esDeLaCasa: boolean
}

/** Un mensaje dentro de un chat. */
export type Mensaje = {
  id: string
  direccion: 'entrante' | 'saliente'
  texto: string | null
  tipo: string
  creadoEn: string
}

/**
 * Realtime del chat.
 *
 * wa_mensajes está en la publicación supabase_realtime (migración 0100) y su RLS
 * (pol_wa_mensajes_ver) solo deja ver a los roles de oficina: la suscripción
 * hereda ese permiso, no abre nada. Cada INSERT invalida las consultas para que
 * el mensaje aparezca solo, sin esperar al refetch. El polling queda de red de
 * seguridad, ya muy espaciado.
 */
function useRealtimeWaMensajes(numeroAbierto: string | null) {
  const qc = useQueryClient()
  useEffect(() => {
    const canal = supabase
      .channel('wa-mensajes-vivo')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'wa_mensajes' },
        (payload) => {
          const fila = payload.new as { numero?: string }
          // Siempre refresca la lista de chats (reordena + último mensaje).
          qc.invalidateQueries({ queryKey: ['wa-conversaciones'] })
          // Y el hilo, si el mensaje es del chat abierto.
          if (fila?.numero && fila.numero === numeroAbierto) {
            qc.invalidateQueries({ queryKey: ['wa-mensajes', numeroAbierto] })
          }
        },
      )
      .subscribe()
    return () => {
      supabase.removeChannel(canal)
    }
  }, [qc, numeroAbierto])
}

/** Las conversaciones, como la lista de chats de WhatsApp. Realtime + red de seguridad. */
export function useConversaciones(numeroAbierto: string | null) {
  useRealtimeWaMensajes(numeroAbierto)

  const consulta = useQuery({
    queryKey: ['wa-conversaciones'],
    refetchInterval: 30_000,
    queryFn: async (): Promise<Conversacion[]> => {
      const { data, error } = await supabase
        .from('vista_wa_conversaciones')
        .select('*')
        .order('ultimo_en', { ascending: false })

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as Record<string, unknown>[]).map((c) => ({
        numero: c.numero as string,
        titulo: c.titulo as string,
        ultimoTexto: (c.ultimo_texto as string | null) ?? null,
        ultimaDireccion: c.ultima_direccion as 'entrante' | 'saliente',
        ultimoEn: c.ultimo_en as string,
        total: Number(c.total ?? 0),
        ordenTrabajoId: (c.orden_trabajo_id as string | null) ?? null,
        sePuedeEscribir: c.se_puede_escribir === true,
        atendidaPor: (c.atendida_por as string | null) ?? null,
        atendidaPorNombre: (c.atendida_por_nombre as string | null) ?? null,
        esDeLaCasa: c.es_de_la_casa === true,
      }))
    },
  })

  return { conversaciones: consulta.data ?? [], cargando: consulta.isLoading, error: consulta.error?.message ?? null }
}

/** Los mensajes de un chat, del más viejo al más nuevo (para apilarlos). */
export function useMensajes(numero: string | null) {
  const consulta = useQuery({
    queryKey: ['wa-mensajes', numero],
    enabled: Boolean(numero),
    refetchInterval: 30_000,
    queryFn: async (): Promise<Mensaje[]> => {
      const { data, error } = await supabase
        .from('wa_mensajes')
        .select('id, direccion, texto, tipo, creado_en')
        .eq('numero', numero!)
        .order('creado_en', { ascending: true })

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as Record<string, unknown>[]).map((m) => ({
        id: m.id as string,
        direccion: m.direccion as 'entrante' | 'saliente',
        texto: (m.texto as string | null) ?? null,
        tipo: m.tipo as string,
        creadoEn: m.creado_en as string,
      }))
    },
  })

  return { mensajes: consulta.data ?? [], cargando: consulta.isLoading, error: consulta.error?.message ?? null }
}


/**
 * ATENDER LA CONVERSACIÓN A MANO.
 *
 * Escribir ya la toma —lo hace la propia función que envía—, así que aquí solo
 * están el mandar y el devolvérsela al asistente. Tomarla sin escribir también
 * sirve: se anuncia que hay alguien antes de teclear la respuesta.
 */
export function useResponderAMano() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({ numero, texto }: { numero: string; texto: string }) => {
      const { data: sesion } = await supabase.auth.getSession()
      const jwt = sesion.session?.access_token

      const r = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-humano`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt ?? ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ numero, texto }),
        },
      )

      const cuerpo = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(cuerpo?.error ?? 'No se pudo enviar el mensaje.')
      return true
    },
    onSuccess: async (_d, { numero }) => {
      await Promise.all([
        clienteConsultas.invalidateQueries({ queryKey: ['wa-conversaciones'] }),
        clienteConsultas.invalidateQueries({ queryKey: ['wa-mensajes', numero] }),
      ])
    },
  })
}

/** Tomar la conversación o devolvérsela al asistente. */
export function useMandoDeLaConversacion() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({ numero, tomar }: { numero: string; tomar: boolean }) => {
      const { error } = await supabase.rpc(tomar ? 'fn_wa_tomar' : 'fn_wa_devolver', {
        p_numero: numero,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['wa-conversaciones'] })
    },
  })
}
